import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"

import { cn } from "@/lib/utils"

function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default"
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "cy:border-input cy:data-[placeholder]:text-muted-foreground cy:[&_svg:not([class*=text-])]:text-muted-foreground cy:focus-visible:border-ring cy:focus-visible:ring-ring/50 cy:aria-invalid:ring-destructive/20 cy:dark:aria-invalid:ring-destructive/40 cy:aria-invalid:border-destructive cy:dark:bg-input/30 cy:dark:hover:bg-input/50 cy:flex cy:w-fit cy:items-center cy:justify-between cy:gap-2 cy:rounded-md cy:border cy:bg-transparent cy:px-3 cy:py-2 cy:text-sm cy:whitespace-nowrap cy:shadow-xs cy:transition-[color,box-shadow] cy:outline-none cy:focus-visible:ring-[3px] cy:disabled:cursor-not-allowed cy:disabled:opacity-50 cy:data-[size=default]:h-9 cy:data-[size=sm]:h-8 cy:*:data-[slot=select-value]:line-clamp-1 cy:*:data-[slot=select-value]:flex cy:*:data-[slot=select-value]:items-center cy:*:data-[slot=select-value]:gap-2 cy:[&_svg]:pointer-events-none cy:[&_svg]:shrink-0 cy:[&_svg:not([class*=size-])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="cy:size-4 cy:opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "cy:bg-popover cy:text-popover-foreground cy:data-[state=open]:animate-in cy:data-[state=closed]:animate-out cy:data-[state=closed]:fade-out-0 cy:data-[state=open]:fade-in-0 cy:data-[state=closed]:zoom-out-95 cy:data-[state=open]:zoom-in-95 cy:data-[side=bottom]:slide-in-from-top-2 cy:data-[side=left]:slide-in-from-right-2 cy:data-[side=right]:slide-in-from-left-2 cy:data-[side=top]:slide-in-from-bottom-2 cy:relative cy:z-50 cy:max-h-(--radix-select-content-available-height) cy:min-w-[8rem] cy:origin-(--radix-select-content-transform-origin) cy:overflow-x-hidden cy:overflow-y-auto cy:rounded-md cy:border cy:shadow-md",
          position === "popper" &&
            "cy:data-[side=bottom]:translate-y-1 cy:data-[side=left]:-translate-x-1 cy:data-[side=right]:translate-x-1 cy:data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "cy:p-1",
            position === "popper" &&
              "cy:h-[var(--radix-select-trigger-height)] cy:w-full cy:min-w-[var(--radix-select-trigger-width)] cy:scroll-my-1"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("cy:text-muted-foreground cy:px-2 cy:py-1.5 cy:text-xs", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "cy:focus:bg-accent cy:focus:text-accent-foreground cy:[&_svg:not([class*=text-])]:text-muted-foreground cy:relative cy:flex cy:w-full cy:cursor-default cy:items-center cy:gap-2 cy:rounded-sm cy:py-1.5 cy:pr-8 cy:pl-2 cy:text-sm cy:outline-hidden cy:select-none cy:data-[disabled]:pointer-events-none cy:data-[disabled]:opacity-50 cy:[&_svg]:pointer-events-none cy:[&_svg]:shrink-0 cy:[&_svg:not([class*=size-])]:size-4 cy:*:[span]:last:flex cy:*:[span]:last:items-center cy:*:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <span className="cy:absolute cy:right-2 cy:flex cy:size-3.5 cy:items-center cy:justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="cy:size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("cy:bg-border cy:pointer-events-none cy:-mx-1 cy:my-1 cy:h-px", className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "cy:flex cy:cursor-default cy:items-center cy:justify-center cy:py-1",
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="cy:size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "cy:flex cy:cursor-default cy:items-center cy:justify-center cy:py-1",
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="cy:size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
